<template>
    <div class="space-y-6 pb-8">
        <div class="-mb-4 pl-2 text-2xl font-semibold uppercase tracking-wide">
            {{ planTitle }}
        </div>

        <plan-presentation
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                :warning-text="$t('purchaseContract.duplicateAgreementWarning')"
                :edit-text="$t('purchaseContract.editAgreement')"
            />

            <select-agreement-participants
                v-if="activeSection > 0"
                :is-active-section="activeSection === 1"
                :contract="contractData"
                :plan="planData"
                :is-mutating-contract="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 1"
            />

            <template v-if="contractData !== undefined">
                <participant-details
                    v-if="activeSection > 1"
                    :is-active-section="activeSection === 2"
                    :contract="contractData"
                    :plan="planData"
                    :is-editing="false"
                    :is-updating-contract="isUpdatingContract"
                    @continue="handleUpdateContract"
                    @edit-section="activeSection = 2"
                />

                <document-section
                    v-if="activeSection > 2"
                    :contract="contractData"
                    :is-signing-document="false"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchaseAgreementPlanFragment = graphql(/** GraphQL */ `
    fragment PurchaseAgreementPlanFragment on Plan {
        id
        name
        ...SelectAgreementParticipantsPlanFragment
        ...PlanPresentationPlanFragment
        ...ParticipantDetailsPlanFragment
        presentation(input: $input, language: $language) {
            title
        }
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
    }
`)

export const PurchaseAgreementContractFragment = graphql(/** GraphQL */ `
    fragment PurchaseAgreementContractFragment on PlanContract {
        id
        ...SelectAgreementParticipantsContractFragment
        ...ParticipantDetailsContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
    }
`)
</script>

<script setup lang="ts">
import type { PropType } from 'vue'
import { computed, ref, watch } from 'vue'
import SelectAgreementParticipants from '~/components/PurchaseContract/Agreement/SelectAgreementParticipants.vue'
import ParticipantDetails from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetails.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import type {
    ChangeContractInput,
    CreateContractInput,
    PurchaseAgreementContractFragmentFragment,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { setCartVisible, useSetCartData } from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useAgreementUpdateContractMutation } from '~/components/PurchaseContract/Agreement/use-agreement-update-contract-mutation'
import { useAgreementChangeContractMutation } from '~/components/PurchaseContract/Agreement/use-agreement-change-contract-mutation'
import { useAgreementCreateContractMutation } from '~/components/PurchaseContract/Agreement/use-agreement-create-contract-mutation'
import { useHead, useOrgShortTitle, useStorefrontPrefix } from '#imports'

const props = defineProps({
    plan: {
        type: Object as PropType<
            FragmentType<typeof PurchaseAgreementPlanFragment>
        >,
        required: true
    }
})

const planData = computed(() =>
    getFragmentData(PurchaseAgreementPlanFragment, props.plan)
)

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    script: [
        { src: 'https://fast.wistia.com/assets/external/E-v1.js', async: true }
    ],
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const allowBookings = ref(planData.value.checkAllowBookings.allowBookings)

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            reset()
        }
    }
)

function reset() {
    // close all the components
    activeSection.value = 1
    // Remove contract data
    contractData.value = undefined
}

const activeSection = ref(1)

const contractData = ref<PurchaseAgreementContractFragmentFragment>()

const setCartData = useSetCartData()
const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

const { mutate: createContract, isPending: isCreatingContract } =
    useAgreementCreateContractMutation()

function handleCreateContract(mutationData: CreateContractInput) {
    createContract(mutationData, {
        onSuccess: data => {
            contractData.value = getFragmentData(
                PurchaseAgreementContractFragment,
                data
            )
            activeSection.value = 2
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    useAgreementChangeContractMutation()

function handleChangeContract(mutationData: ChangeContractInput) {
    changeContract(mutationData, {
        onSuccess: data => {
            if (data.changeContract.__typename === 'ChangeContractResult') {
                // Update the cart data
                if (data.changeContract.query.viewer !== null) {
                    setCartData(data.changeContract.query.viewer.cart)
                }
                if (data.changeContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseAgreementContractFragment,
                        data.changeContract.record
                    )
                    activeSection.value = 2
                    return
                }
            }

            throw new Error()
        }
    })
}

const { mutate: updateContract, isPending: isUpdatingContract } =
    useAgreementUpdateContractMutation()
function handleUpdateContract(mutationData: UpdateContractInput) {
    updateContract(mutationData, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchaseAgreementContractFragment,
                        data.updateContract.record
                    )
                    activeSection.value = 3
                    return
                }
            }

            throw new Error()
        }
    })
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())
</script>
